import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface HeatMapCell {
  category: string;
  department: string;
  count: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface RiskHeatMapProps {
  data: HeatMapCell[];
  className?: string;
}

const departments = ['Technology', 'Finance', 'Operations', 'Compliance', 'HR'];
const categories = ['Operational', 'Financial', 'Strategic', 'Compliance', 'Reputational'];

const getSeverityColor = (severity: string, count: number) => {
  if (count === 0) return 'bg-neutral-900';
  
  switch (severity) {
    case 'critical':
      return 'bg-red-600 hover:bg-red-500';
    case 'high':
      return 'bg-orange-600 hover:bg-orange-500';
    case 'medium':
      return 'bg-yellow-600 hover:bg-yellow-500';
    case 'low':
      return 'bg-green-700 hover:bg-green-600';
    default:
      return 'bg-neutral-800';
  }
};

export function RiskHeatMap({ data, className }: RiskHeatMapProps) {
  const getCell = (category: string, department: string): HeatMapCell | undefined => {
    return data.find(cell => cell.category === category && cell.department === department);
  };

  return (
    <Card className={cn("p-6", className)}>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white">Risk Heat Map</h3>
        <p className="text-sm text-neutral-400 mt-1">Risk concentration by category and department</p>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Header Row */}
          <div className="flex items-center mb-2">
            <div className="w-32 flex-shrink-0"></div>
            {departments.map(dept => (
              <div key={dept} className="flex-1 min-w-[100px] px-2">
                <p className="text-xs font-medium text-neutral-400 text-center">{dept}</p>
              </div>
            ))}
          </div>

          {/* Heat Map Grid */}
          <div className="space-y-2">
            {categories.map(category => (
              <div key={category} className="flex items-center">
                <div className="w-32 flex-shrink-0 pr-4">
                  <p className="text-sm font-medium text-neutral-300">{category}</p>
                </div>
                {departments.map(dept => {
                  const cell = getCell(category, dept);
                  const count = cell?.count || 0;
                  const severity = cell?.severity || 'low';
                  
                  return (
                    <div key={`${category}-${dept}`} className="flex-1 min-w-[100px] px-2">
                      <div
                        className={cn(
                          "h-16 rounded-lg transition-all duration-200 cursor-pointer",
                          "flex items-center justify-center border border-neutral-800",
                          getSeverityColor(severity, count)
                        )}
                        title={`${category} - ${dept}: ${count} risks (${severity})`}
                      >
                        <span className="text-sm font-bold text-white">
                          {count > 0 ? count : '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-neutral-800">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-700"></div>
              <span className="text-xs text-neutral-400">Low</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-600"></div>
              <span className="text-xs text-neutral-400">Medium</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-600"></div>
              <span className="text-xs text-neutral-400">High</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-600"></div>
              <span className="text-xs text-neutral-400">Critical</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
